import { useState, useEffect } from "react";
import { SoftwareVersioning } from "./SoftwareVersioning";
import { Category, RowData } from "./RowData";

interface VersionInfo {
  ProductionRelease2: string;
  ProductionReleaseDate2: string;
  MFGRelease2: string;
  MFGReleaseDate2: string;
  ProductionRelease: string;
  ProductionReleaseDate: string;
  MFGRelease: string;
  MFGReleaseDate: string;
  EncoreProductionRelease: string;
  EncoreProductionReleaseDate: string;
  EncoreMFGRelease: string;
  EncoreMFGReleaseDate: string;
  Encore2ProductionRelease: string;
  Encore2ProductionReleaseDate: string;
  Encore2MFGRelease: string;
  Encore2MFGReleaseDate: string;
  ProductionRelease3: string;
  ProductionReleaseDate3: string;
  MFGRelease3: string;
  MFGReleaseDate3: string;
}

export const useVersionInfo = () => {
  const [versionInfo, setVersionInfo] = useState<VersionInfo>();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          "https://nnt.myenovate.com/api/StoredProc/",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json; charset=utf-8",
            },
            body: JSON.stringify({
              storedProcName: "dbo.prcGetEnvoyVersionInfo",
            }),
          }
        );

        if (!response.ok) {
          setError("Unable to connect.");
          return;
        }

        const dataJson: VersionInfo = (await response.json())[0];
        setVersionInfo(dataJson);
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Error fetching data");
      }
    };

    fetchData();
  }, []);

  return { versionInfo, error };
};

export const useVersionData = (): SoftwareVersioning[] => {
  const { versionInfo: info } = useVersionInfo();

  // "changes" field is not provided by the API and will only read from global type
  return [
    {
      name: "Encore 2.0",
      type: "Global",
      number: `${info?.Encore2ProductionRelease}`,
      versionDate: new Date(`${info?.Encore2ProductionReleaseDate}`),
      description:
        "Using the Encore 2.0's touch display, navigate to the System Settings screen to view the software version installed on the workstation.",
      changes: `encore2`,
    },
    {
      name: "Encore 2.0",
      type: "MFG",
      number: `${info?.Encore2MFGRelease}`,
      versionDate: new Date(`${info?.Encore2MFGReleaseDate}`),
      description:
        "Using the Encore 2.0's touch display, navigate to the System Settings screen to view the software version installed on the workstation.",
    },
    {
      name: "Envoy 2.0",
      type: "Global",
      number: `${info?.ProductionRelease2}`,
      versionDate: new Date(`${info?.ProductionReleaseDate2}`),
      description:
        'Using the Envoy\'s touch display, navigate to the "System Settings" screen to view the software version installed on the workstation.',
      changes: `envoy2`,
    },
    {
      name: "Envoy 2.0",
      type: "MFG",
      number: `${info?.MFGRelease2}`,
      versionDate: new Date(`${info?.MFGReleaseDate2}`),
      description:
        'Using the Envoy\'s touch display, navigate to the "System Settings" screen to view the software version installed on the workstation.',
    },
    {
      name: "Mosaic",
      type: "Global",
      number: `${info?.ProductionRelease3}`,
      versionDate: new Date(`${info?.ProductionReleaseDate3}`),
      description:
        'Using the Mosaic\'s touch display, navigate to the "System Settings" screen to view the software version installed on the workstation.',
      changes: `mosaic`,
    },
    {
      name: "Mosaic",
      type: "MFG",
      number: `${info?.MFGRelease3}`,
      versionDate: new Date(`${info?.MFGReleaseDate3}`),
      description:
        'Using the Mosaic\'s touch display, navigate to the "System Settings" screen to view the software version installed on the workstation.',
    },
    {
      name: "Envoy",
      type: "Global",
      number: `${info?.ProductionRelease}`,
      versionDate: new Date(`${info?.ProductionReleaseDate}`),
      description:
        'Using the Envoy\'s touch display, navigate to the "System Settings" screen to view the software version installed on the workstation.',
      changes: `envoy`,
    },
    {
      name: "Envoy",
      type: "MFG",
      number: `${info?.MFGRelease}`,
      versionDate: new Date(`${info?.MFGReleaseDate}`),
      description:
        'Using the Envoy\'s touch display, navigate to the "System Settings" screen to view the software version installed on the workstation.',
    },
    {
      name: "Encore",
      type: "Global",
      number: `${info?.EncoreProductionRelease}`,
      versionDate: new Date(`${info?.EncoreProductionReleaseDate}`),
      description:
        "Restart the Encore EcoFlex Workstation to see the software version displayed on the LCD.",
      changes: `encore`,
    },
    {
      name: "Encore",
      type: "MFG",
      number: `${info?.EncoreMFGRelease}`,
      versionDate: new Date(`${info?.EncoreMFGReleaseDate}`),
      description:
        "Restart the Encore EcoFlex Workstation to see the software version release date displayed on the LCD.",
    },
  ];
};

export const useData = (): RowData[] => {
  return [
    {
      name: "Asset Control Desktop Application",
      update: new Date("10-02-2025"),
      description:
        "Asset Control is a Windows desktop application allowing users to setup and configure their RhythmConnect-ready workstations.",
      url: "https://assetcontrol.ergotron.com/",
      category: Category.app,
    },
    {
      name: "Asset Control Mobile (iOS)",
      update: new Date("02-12-2025"),
      description:
        "Asset Control Mobile for iOS gives administrators a powerful toolset in order to manage all RhythmConnect-ready devices.",
      url: "https://apps.apple.com/us/app/ergotron-asset-control/id6587399353",
      category: Category.app,
    },
    {
      name: "Mosaic Software User Guide",
      update: new Date("10-06-2025"),
      description:
        "This comprehensive guide covers the suite of software tools available for the Mosaic Workstation including the Touch Screen User Interface and Asset Control for Desktop and iOS.",
      url: "https://software.ergotron.com/files/Mosaic_manual.pdf",
      category: Category.doc,
    },
    {
      name: "Encore 2.0 Software User Guide",
      update: new Date("05-21-2025"),
      description:
        "This comprehensive guide covers the suite of software tools available for the Encore 2.0 Workstation including the Touch Screen User Interface and all on-device features.",
      url: "https://software.ergotron.com/files/Encore2_Manual.pdf",
      category: Category.doc,
    },
    {
      name: "Envoy 2.0 Software User Guide",
      update: new Date("05-04-2023"),
      description:
        "This comprehensive guide covers the suite of software tools available for the Envoy 2.0 Workstation including the Touch Screen User Interface, the emControl Desktop Application, emControl for Android, and Rhythm Unplugged for iOS.",
      url: "http://software.myenovate.com/files/Envoy2SoftwareUserGuide.pdf",
      category: Category.doc,
    },
    {
      name: "Envoy 1.0 Software User Guide",
      update: new Date("04-28-2020"),
      description:
        "This comprehensive guide covers the suite of software tools available for the Envoy Workstation including the Touch Screen User Interface, the emControl Desktop Application, emControl for Android, and Rhythm Unplugged for iOS.",
      url: "http://software.myenovate.com/files/EnvoySoftwareUserGuide.pdf",
      category: Category.doc,
    },
    {
      name: "Encore EcoFlex Software User Guide",
      update: new Date("05-04-2020"),
      description:
        "This comprehensive guide covers the suite of software tools available for the Encore EcoFlex including the LCD display, the emControl Desktop Application, emControl for Android, and Rhythm Unplugged for iOS.",
      url: "http://software.myenovate.com/files/EcoflexEncoreUserGuide.pdf",
      category: Category.doc,
    },
    {
      name: "PulseTech Configuration Utility",
      update: new Date("02-20-2019"),
      description:
        "A Windows utility used for configuring MobiusPower powered devices.",
      url: "https://pulsetechconfig.myenovate.com/",
      category: Category.app,
    },
    {
      name: "MobiusPower Scout",
      update: new Date("11-12-2021"),
      description:
        "A Windows utility used for viewing MobiusPower battery via the MobiusPower Bay Charger.",
      url: "https://mobiusscout.myenovate.com/",
      category: Category.app,
    },
    {
      name: "MobiusPower Status Monitor 4.3",
      update: new Date("03-17-2016"),
      description:
        "A Windows utility that installs the graphical battery charge indicator on the desktop and initiates a graceful shutdown of Windows when the Mobius battery reaches a low charge level. This utility is for non-EcoFlex V3 MobiusPower Powered Workstations only and is not compatible with Envoy nor Encore EcoFlex workstations.",
      url: "https://software.myenovate.com/files/zipfiles/MobiusStatusMonitor43.ZIP",
      category: Category.app,
    },
    {
      name: "USB Bootloader Update Application",
      update: new Date("03-25-2022"),
      description:
        "A Windows utility for updating MobiusPower workstations and Charger bays offline. Not compatible with Envoy nor Encore EcoFlex workstations.",
      url: "https://software.myenovate.com/files/zipfiles/USBBootLoader.zip",
      category: Category.app,
    },
    {
      name: "MobiusPower Update Utility",
      update: new Date("03-25-2022"),
      description:
        "A Windows utility for updating MobiusPower workstations and Charger bays. Not compatible with Envoy nor Encore EcoFlex workstations.",
      url: "https://software.myenovate.com/files/zipfiles/MobiusUpdateUtility.zip",
      category: Category.app,
    },
    {
      name: "R7 Drivers",
      update: new Date("10-17-2018"),
      description: "Drivers for the R7 All-In-One PC.",
      url: "http://software.myenovate.com/files/zipfiles/R7Drivers.rar",
      category: Category.drive,
    },
    {
      name: "R6 Drivers",
      update: new Date("09-21-2014"),
      description: "Drivers for the R6 All-In-One PC (All Revisions).",
      url: "http://software.myenovate.com/files/zipfiles/R6Drivers.rar",
      category: Category.drive,
    },
    {
      name: "Intel NUC Drivers for Windows 10 64-Bit PC",
      update: new Date("09-19-2018"),
      description: "Driver Bundle for Intel NUC running Windows 10 64-BIT.",
      url: "https://downloadcenter.intel.com/product/128700/Intel-NUC-7-Home-a-Mini-PC-with-Windows-10-NUC7i5BNKP",
      category: Category.drive,
    },
    {
      name: "Intel NUC Drivers for Windows 7 64-bit PC",
      update: new Date("09-19-2018"),
      description:
        "Enovate distributed Driver Bundle for Intel NUC running Windows 7 64-BIT.",
      url: "http://software.myenovate.com/files/zipfiles/NUCDrivers.zip",
      category: Category.drive,
    },
    {
      name: "R7 - Windows 7/Skylake Patch",
      update: new Date("09-22-2018"),
      description:
        'The <a href="#intel-nuc-drivers-for-windows-7-64-bit-pc" class="inline-link">Windows 7 installation media</a> does not include native driver support for USB 3.0, so during installation, when you get to the screen to select your preferred language, any keyboard or mouse connected to a USB 3.0 port will not respond. (Affects R7 All-In-One)',
      url: "https://www.intel.com/content/www/us/en/support/boards-and-kits/intel-nuc-kits/000017241.html",
      category: Category.drive,
      renderDescription: true,
    },
    {
      name: "RhythmConnect Manual",
      update: new Date("02-07-2025"),
      description:
        "This user manual provides you with the information needed to understand RhythmConnect Fleet Control and to quickly start using it to manage your assets, including batteries, chargers, and workstations. It provides a system overview and procedures to complete system management tasks. Note that the RhythmConnect application is responsive, and as such, the display on your device may be different than that shown in this manual.",
      url: "https://software.ergotron.com/files/RhythmConnectManual.pdf",
      category: Category.doc,
    },
    {
      name: "PulseTech Web Manual",
      update: new Date("05-10-2019"),
      description:
        "This user manual provides you with the information needed to understand PulseTech Web and includes a guide on how to configure your Enovate devices to communicate over a wireless network using PulseTech Config.",
      url: "http://software.myenovate.com/files/PulseTechWeb.pdf",
      category: Category.doc,
    },
    {
      name: "Device Configuration Guide",
      update: new Date("05-20-2019"),
      description:
        "A Guide for configuring your Non-EcoFlex powered devices using the PulseTech Configuration Tool.",
      url: "http://software.myenovate.com/files/DeviceConfigurationGuide.pdf",
      category: Category.doc,
    },
    {
      name: "MobiusPower Scout Guide",
      update: new Date("03-12-2020"),
      description:
        "This guide provides instructions for using the MobiusPower Scout Application.",
      url: "http://software.myenovate.com/files/MobiusPowerScout.pdf",
      category: Category.doc,
    },
    {
      name: "Communication And Security Overview",
      update: new Date("04-20-2020"),
      description:
        "An overview of RhythmConnect architecture and security; including technical information for configuring the firewall at a RhythmConnect-enabled site.",
      url: "https://software.ergotron.com/files/Network_Security_Overview.pdf",
      category: Category.doc,
    },
    {
      name: "Applied Power EON Safety Data Sheet",
      update: new Date("07-01-2011"),
      description: "Safety Data Sheet for Applied Power's EON LifePO4 Battery.",
      url: "http://software.myenovate.com/files/AppliedPowerEONLifePO4SDS.pdf",
      category: Category.safeCert,
    },
    {
      name: "Inventus Power Safety Data Sheet",
      update: new Date("09-24-2013"),
      description:
        "Safety Data Sheet for Inventus Power's EON LifePO4 Battery.",
      url: "http://software.myenovate.com/files/InventusPowerLifePO4SDS.pdf",
      category: Category.safeCert,
    },
    {
      name: "Mobius 4.0 Battery Safety Data Sheet",
      update: new Date("09-26-2016"),
      description:
        "Safety Data Sheet for Enovate Medical's Mobius 4.0 Lithium Ion Swappable Battery.",
      url: "http://software.myenovate.com/files/Mobius40SDS.pdf",
      category: Category.safeCert,
    },
    {
      name: "Panasonic SLA Battery Safety Data Sheet",
      update: new Date("08-20-2015"),
      description: "Safety Data Sheet for Panasonic SLA Battery.",
      url: "http://software.myenovate.com/files/PANASONICVRLASDS.pdf",
      category: Category.safeCert,
    },
    {
      name: "Power Sonic PSL-12500 Safety Data Sheet Sheet",
      update: new Date("06-08-2018"),
      description:
        "Safety Data Sheet for Power Sonic PSL-12500 LiFePO Battery.",
      url: "http://software.myenovate.com/files/PSL12500.pdf",
      category: Category.safeCert,
    },
    {
      name: "Valence LifePO4 Safety Data Sheet",
      update: new Date("01-24-2014"),
      description: "Safety Data Sheet for Valence's LifePO4 Battery.",
      url: "http://software.myenovate.com/files/VALENCELiFePO4SDS.pdf",
      category: Category.safeCert,
    },
    {
      name: "Powersonic's AMG SLA Battery Safety Data Sheet",
      update: new Date("05-01-2017"),
      description: "Safety Data Sheet for PowerSonic's AMG SLA Battery.",
      url: "http://software.myenovate.com/files/PowersonicVRLASDS.pdf",
      category: Category.safeCert,
    },
    {
      name: "Seismic Certification: e550, e750, e850",
      update: new Date("10-15-2013"),
      description:
        "Seismic Certification Sheet for the e550, e750,& e850 WallStations.",
      url: "http://software.myenovate.com/files/e550e750e850SeismicCerts.pdf",
      category: Category.safeCert,
    },
    {
      name: "Seismic Certification: e997",
      update: new Date("11-06-2012"),
      description: "Seismic Certification Sheet for the e997 Wall Arm.",
      url: "http://software.myenovate.com/files/e997SeismicCert.pdf",
      category: Category.safeCert,
    },
    {
      name: "UL60601-1 Certification: EMC",
      update: new Date("10-18-2016"),
      description:
        "UL 60601-1 Certification Sheet for Enovate Medical's EMC Workstation.",
      url: "http://software.myenovate.com/files/EMC60601ULCertEd2.pdf",
      category: Category.safeCert,
    },
    {
      name: "ES60601-1 Certification: Encore",
      update: new Date("06-20-2018"),
      description:
        "ES 60601-1 Certification Sheet for Enovate Medical's Encore Workstation.",
      url: "http://software.myenovate.com/files/Encore60601ESCertEd3.pdf",
      category: Category.safeCert,
    },
    {
      name: "ES60601-1 Certification: Encore",
      update: new Date("02-14-2020"),
      description:
        "ES 60601-1 Certification Sheet for Encore Workstation with EcoFlex.",
      url: "http://software.myenovate.com/files/EncoreEcoFlex60601ESCerEd3.pdf",
      category: Category.safeCert,
    },
    {
      name: "ES60601-1 Certification: Envoy",
      update: new Date("06-07-2018"),
      description:
        "ES 60601-1 Certification Sheet for Enovate Medical's Envoy Workstation.",
      url: "http://software.myenovate.com/files/Envoy60601ESCertEd3.pdf",
      category: Category.safeCert,
    },
    {
      name: "UL60601-1 Certification: SydeKick",
      update: new Date("04-14-2014"),
      description:
        "UL 60601-1 Certification Sheet for Enovate Medical's SydeKick Workstation.",
      url: "http://software.myenovate.com/files/Sydekick60601ULCertEd2.pdf",
      category: Category.safeCert,
    },
    {
      name: "CB60601-1 Certification: SydeKick",
      update: new Date("05-20-2014"),
      description:
        "CB 60601-1 Certification Sheet for Enovate Medical's SydeKick Workstation..",
      url: "http://software.myenovate.com/files/Sydekick60601CBCertEd2.pdf",
      category: Category.safeCert,
    },
    {
      name: "CB62133 Certification: Mobius 3.5 Battery",
      update: new Date("08-12-2014"),
      description:
        "CB 62133 Certification Sheet for Enovate Medical's Mobius 3.5 Lithium Ion Swappable Battery.",
      url: "http://software.myenovate.com/files/Mobius3562133CBCertEd2.pdf",
      category: Category.safeCert,
    },
    {
      name: "CB62133 Certification: Mobius 4.0 Battery",
      update: new Date("12-15-2015"),
      description:
        "CB 62133 Certification Sheet for Enovate Medical's Mobius 4.0 Lithium Ion Swappable Battery.",
      url: "http://software.myenovate.com/files/Mobius4062133CBCertEd2.pdf",
      category: Category.safeCert,
    },
    {
      name: "Compliance Verification: Mobius 3.5 Battery",
      update: new Date("06-04-2014"),
      description:
        "Verification of Compliance Sheet for Enovate Medical's Mobius 3.5 Lithium Ion Swappable Battery.",
      url: "http://software.myenovate.com/files/Mobius35UN383UNVoCEd5A1.pdf",
      category: Category.safeCert,
    },
    {
      name: "Compliance Verification: Mobius 4.0 Battery",
      update: new Date("11-17-2015"),
      description:
        "Verification of Compliance Sheet for Enovate Medical's Mobius 4.0 Lithium Ion Swappable Battery.",
      url: "http://software.myenovate.com/files/Mobius40UN383UNVoCEd5A2.pdf",
      category: Category.safeCert,
    },
    {
      name: "UL62133 Certification: Mobius 4.0 Battery",
      update: new Date("06-15-2016"),
      description:
        "UL 62133 Certification Sheet for Enovate Medical's Mobius 4.0 Lithium Ion Swappable Battery.",
      url: "http://software.myenovate.com/files/Mobius4062133ULCert.pdf",
      category: Category.safeCert,
    },
    {
      name: "UL1012 Certification for Mobius Bay Charger",
      update: new Date("06-21-2014"),
      description:
        "UL 1012 Certification Sheet for Enovate Medical's Mobius Battery Bay Charger.",
      url: "http://software.myenovate.com/files/MobiusCharger1012ULCertEd8.pdf",
      category: Category.safeCert,
    },
  ];
};
